import { getUserToken } from '../firebase';
import { generateUrl } from '@/utils/generateUrl';
import { getIpv4Address, getIpv6Address } from '../getIpAddress';
import { ErrorResponseBody, ResponseBody } from '../types/ResponseBody';
import { getAppVersion } from '@/utils/getAppVersion';
import { getBuildVersion } from '@/utils/getBuildVersion';
import { WEB_SESSION } from '@/utils/browserStorageNames';
import { tokenStore } from '@/services/tokenStore';

const UNAUTHORIZED = 401;
const AUTH_REFRESH_LOCK = 'auth:refresh';

class BaseApi {
  private baseUrl: string;
  private deviceId: string = '';
  private refreshPromise: Promise<void> | null = null;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL ?? '';
  }

  private async getDeviceId() {
    if (!this.deviceId) {
      const { ip: ipv4 } = (await getIpv4Address()) ?? '';
      const { ip: ipv6 } = (await getIpv6Address()) ?? '';
      this.deviceId = `${ipv4},${ipv6}`;
    }
  }

  public async getHeaders() {
    await this.getDeviceId();

    const userToken = await getUserToken();
    const accessToken = tokenStore.getAccessToken();
    return {
      'Content-Type': 'application/json',
      'Device-OS': 'web',
      'App-Build': getBuildVersion(),
      'App-Version': getAppVersion(),
      'Device-Id': this.deviceId,
      ...(userToken
        ? {
            Authorization: `Bearer ${userToken}`,
            Signature: sessionStorage.getItem(WEB_SESSION) ?? ''
          }
        : {}),
      ...(accessToken
        ? {
            'SF-Authorization': `Bearer ${accessToken}`
          }
        : {})
    };
  }

  public async getMultiPartHeaders() {
    await this.getDeviceId();

    const userToken = await getUserToken();
    const accessToken = tokenStore.getAccessToken();
    return {
      'Device-OS': 'web',
      'App-Build': getBuildVersion(),
      'App-Version': getAppVersion(),
      'Device-Id': this.deviceId,
      ...(userToken
        ? {
            Authorization: `Bearer ${userToken}`,
            Signature: sessionStorage.getItem(WEB_SESSION) ?? ''
          }
        : {}),
      ...(accessToken
        ? {
            'SF-Authorization': `Bearer ${accessToken}`
          }
        : {})
    };
  }

  private async refreshAccessToken(): Promise<void> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        if (typeof navigator.locks === 'undefined') {
          await this.performRefresh();
          return;
        }
        await navigator.locks.request(AUTH_REFRESH_LOCK, () => this.performRefresh());
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  private async performRefresh(): Promise<void> {
    // Read inside the lock: a tab that waited needs the token rotated by the winner.
    const csrfToken = tokenStore.getCsrfToken();
    const url = generateUrl('api/v1/refresh/web', this.baseUrl);

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Device-OS': 'web',
        'App-Build': getBuildVersion(),
        'App-Version': getAppVersion(),
        ...(csrfToken
          ? {
              'X-CSRF-Token': csrfToken
            }
          : {})
      }
    });

    if (!response.ok) {
      window.dispatchEvent(new Event('auth:refresh-failed'));
      throw new Error('Refresh failed.');
    }

    const data = await response.json();
    tokenStore.setAccessToken(data.data.accessToken);
    tokenStore.setCsrfToken(data.data.csrfToken);
  }

  private async parseResponse<T>(response: Response): Promise<ResponseBody<T>> {
    if (!response.ok) {
      let json: ErrorResponseBody;
      try {
        json = await response.json();
      } catch {
        throw response;
      }
      // response.status is the real numeric HTTP status; json.status is a string enum name
      // (e.g. 'INTERNAL_SERVER_ERROR') and json.code is an unrelated business error code, so
      // neither can be parsed as a number. Attach it separately for callers that need to
      // distinguish retryable (5xx) from permanent (4xx) failures.
      throw { ...json, httpStatus: response.status };
    }

    const contentLength = response.headers.get('content-length');
    if (response.status === 204 || contentLength === '0') {
      return { success: true } as ResponseBody<T>;
    }
    return response.json();
  }

  private async handleRequest<T>(path: string, options: RequestInit): Promise<ResponseBody<T>> {
    const url = generateUrl(path, this.baseUrl);
    const response = await fetch(url, { ...options, credentials: 'include' });

    if (response.status === UNAUTHORIZED) {
      await this.refreshAccessToken();
      const retryHeaders = await this.getHeaders();
      const retryResponse = await fetch(url, {
        ...options,
        headers: retryHeaders,
        credentials: 'include'
      });

      return this.parseResponse<T>(retryResponse);
    }

    return this.parseResponse<T>(response);
  }

  async post<TResponse, TRequestBody>(path: string, body: TRequestBody | null) {
    const options: RequestInit = {
      method: 'POST',
      headers: await this.getHeaders(),
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined
    };

    return this.handleRequest<TResponse>(path, options);
  }

  async multiPartPost<TResponse>(path: string, formData: FormData) {
    const options: RequestInit = {
      method: 'POST',
      headers: await this.getMultiPartHeaders(),
      body: formData
    };

    return this.handleRequest<TResponse>(path, options);
  }

  async get<TResponse>(path: string) {
    const options: RequestInit = {
      method: 'GET',
      headers: await this.getHeaders()
    };

    const res = await this.handleRequest<TResponse>(path, options);
    return res;
  }

  async put<TResponse, TRequestBody>(path: string, body: TRequestBody) {
    const options: RequestInit = {
      method: 'PUT',
      headers: await this.getHeaders(),
      body: JSON.stringify(body)
    };

    return this.handleRequest<TResponse>(path, options);
  }

  async patch<TResponse, TRequestBody>(path: string, body: TRequestBody) {
    const options: RequestInit = {
      method: 'PATCH',
      headers: await this.getHeaders(),
      body: JSON.stringify(body)
    };

    return this.handleRequest<TResponse>(path, options);
  }

  async delete<TResponse, TRequestBody>(path: string, body?: TRequestBody) {
    const options: RequestInit = {
      method: 'DELETE',
      headers: await this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined
    };

    return this.handleRequest<TResponse>(path, options);
  }
}

export default new BaseApi();
